/**
 * ElecBid — Login Screen
 * Supports Google OAuth and email+password sign-in.
 * Access is gated by the crew_members table (PermissionsProvider).
 */

import { useState } from "react";
import { supabase } from "../lib/supabase";
import NewVersionNotice from "./NewVersionNotice";

export default function LoginScreen() {
  const [mode, setMode] = useState("google"); // "google" | "email" | "forgot"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  // Email+password form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  async function signInWithGoogle() {
    setLoading(true);
    setError(null);
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/` },
    });
    if (err) { setError(err.message); setLoading(false); }
  }

  async function signInWithEmail(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { error: err } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });
    if (err) { setError(err.message); setLoading(false); }
  }

  // The endpoint always answers { ok: true }, whether or not the address is a real
  // crew account — that is deliberate (it must not confirm which addresses exist).
  // So the UI can only ever show the same neutral confirmation.
  async function sendResetLink(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await fetch("/api/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
    } catch {
      /* deliberately ignored — see above */
    }
    setSent(true);
    setLoading(false);
  }

  function switchMode(next) {
    setMode(next);
    setError(null);
    setSent(false);
    setPassword("");
    if (next !== "forgot") setEmail("");
  }

  return (
    <>
      <style>{CSS}</style>
      <div className="wrap">
        <div className="card">
          {/* A signed-out window can sit here for days; renders nothing until a newer build is deployed. */}
          <NewVersionNotice className="update-note" note="Reload before you sign in." />
          <img src="/elecbid-logo.png" alt="ElecBid" className="logo-img" />

          {/* Mode toggle */}
          <div className="toggle-row">
            <button
              className={`toggle-btn${mode === "google" ? " active" : ""}`}
              onClick={() => switchMode("google")}
              type="button"
            >
              Google
            </button>
            <button
              className={`toggle-btn${mode === "email" || mode === "forgot" ? " active" : ""}`}
              onClick={() => switchMode("email")}
              type="button"
            >
              Email
            </button>
          </div>

          {mode === "google" ? (
            <>
              <button
                className={`btn google-btn${loading ? " loading" : ""}`}
                onClick={signInWithGoogle}
                disabled={loading}
              >
                {loading ? <span className="spinner" /> : (
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                    <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                  </svg>
                )}
                {loading ? "Signing in..." : "Sign in with Google"}
              </button>
              <div className="note">Sign in with the Google account Dylan invited you with.</div>
            </>
          ) : mode === "forgot" ? (
            sent ? (
              <div style={{ width: "100%" }}>
                <div className="sent">
                  If that address belongs to an ElecBid account, a reset link is on its
                  way. Open it, tap <strong>Accept</strong>, and set a new password.
                </div>
                <button type="button" className="linkbtn" onClick={() => switchMode("email")}>
                  ← Back to sign in
                </button>
              </div>
            ) : (
              <form onSubmit={sendResetLink} style={{ width: "100%" }}>
                <input
                  type="email"
                  className="email-input"
                  placeholder="Email address"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
                <button
                  type="submit"
                  className={`btn email-btn${loading ? " loading" : ""}`}
                  disabled={loading}
                >
                  {loading ? <span className="spinner" /> : null}
                  {loading ? "Sending..." : "Send Reset Link"}
                </button>
                <button type="button" className="linkbtn" onClick={() => switchMode("email")}>
                  ← Back to sign in
                </button>
              </form>
            )
          ) : (
            <form onSubmit={signInWithEmail} style={{ width: "100%" }}>
              <input
                type="email"
                className="email-input"
                placeholder="Email address"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              <input
                type="password"
                className="email-input"
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                style={{ marginTop: 10 }}
              />
              <button
                type="submit"
                className={`btn email-btn${loading ? " loading" : ""}`}
                disabled={loading}
              >
                {loading ? <span className="spinner" /> : null}
                {loading ? "Signing in..." : "Sign In"}
              </button>
              <button type="button" className="linkbtn" onClick={() => switchMode("forgot")}>
                Forgot password?
              </button>
            </form>
          )}

          {error && <div className="error">{error}</div>}
        </div>
      </div>
    </>
  );
}

const CSS = `
  *{box-sizing:border-box}
  .wrap{min-height:100vh;background:#0d0f12;display:flex;align-items:center;justify-content:center;padding:24px;font-family:'IBM Plex Sans',system-ui,sans-serif}
  .card{background:#161a1f;border:1px solid rgba(255,255,255,0.08);border-radius:20px;padding:48px 40px;width:100%;max-width:380px;text-align:center;display:flex;flex-direction:column;align-items:center;gap:12px}
  .update-note{width:100%;margin-bottom:8px}
  .logo-img{width:100%;max-width:300px;height:auto;object-fit:contain;border-radius:12px;margin-bottom:4px}
  .title{font-size:28px;font-weight:800;color:#f0f0f0;letter-spacing:-0.5px}
  .sub{font-size:13px;color:#8a919c;margin-bottom:4px}
  .toggle-row{display:flex;gap:4px;background:#0d0f12;border-radius:8px;padding:3px;width:100%;margin-bottom:4px}
  .toggle-btn{flex:1;padding:7px 0;border:none;border-radius:6px;background:transparent;color:#8a919c;font-size:13px;font-weight:600;cursor:pointer;font-family:inherit;transition:all 0.15s}
  .toggle-btn.active{background:#1e2328;color:#f0f0f0}
  .btn{display:flex;align-items:center;justify-content:center;gap:10px;width:100%;padding:13px 20px;border:none;border-radius:10px;font-size:15px;font-weight:600;cursor:pointer;font-family:inherit;margin-top:4px;transition:all 0.15s}
  .google-btn{background:#fff;color:#1a1a1a}
  .google-btn:hover:not(:disabled){background:#f0f0f0;transform:translateY(-1px);box-shadow:0 4px 12px rgba(0,0,0,0.3)}
  .email-btn{background:#a5ce3a;color:#0d0f12;margin-top:12px}
  .email-btn:hover:not(:disabled){background:#b8de4a;transform:translateY(-1px);box-shadow:0 4px 12px rgba(0,0,0,0.3)}
  .btn:disabled{opacity:0.7;cursor:not-allowed}
  .btn.loading{background:#1e2328;color:#f0f0f0}
  .email-input{width:100%;padding:11px 14px;background:#1e2328;border:1px solid rgba(255,255,255,0.08);border-radius:9px;color:#f0f0f0;font-size:14px;font-family:inherit;outline:none;display:block}
  .email-input:focus{border-color:#a5ce3a40}
  .spinner{width:18px;height:18px;border:2px solid rgba(245,158,11,0.3);border-top-color:#f59e0b;border-radius:50%;animation:spin 0.8s linear infinite;flex-shrink:0}
  @keyframes spin{to{transform:rotate(360deg)}}
  .error{font-size:13px;color:#ef4444;background:rgba(239,68,68,0.08);border:1px solid rgba(239,68,68,0.2);border-radius:8px;padding:10px 14px;width:100%}
  .note{font-size:11px;color:#4a5260;margin-top:4px}
  .sent{font-size:13px;color:#a5ce3a;background:rgba(165,206,58,0.08);border:1px solid rgba(165,206,58,0.22);border-radius:8px;padding:12px 14px;width:100%;line-height:1.55;text-align:left}
  .linkbtn{display:block;width:100%;margin-top:12px;padding:4px;background:none;border:none;color:#8a919c;font-size:12px;font-family:inherit;cursor:pointer;text-decoration:underline}
  .linkbtn:hover{color:#f0f0f0}
`;
